import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Receipt, User, UtensilsCrossed, Apple, Images, Info, LayoutDashboard, BookOpen, LogOut, LogIn, MessageCircle, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { PHONE_TEL, waLink } from "@/lib/contact";

const WA_CONTACT_URL = waLink("Hello BIGA, I would like to place an order");

function Row({ icon: Icon, label, active, onClick, testId, big }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-current={active ? "page" : undefined}
      className={`w-full flex items-center gap-3 rounded-2xl px-4 text-left font-bold transition-colors ${big ? "min-h-[56px] text-base" : "min-h-[48px] text-[15px]"} ${active ? "bg-biga-orange text-biga-cream" : "text-biga-ink hover:bg-biga-ink/5"}`}>
      <Icon size={big ? 20 : 18} className="shrink-0" /> {label}
    </button>
  );
}

// Phone/small-tablet slide-out menu. Back button closes it (a history entry is pushed while open).
export default function MobileMenu({ open, onOpenChange, onMenu, onAuthClick }) {
  const { user, logout } = useAuth();
  const { count, startWhatsAppCheckout } = useCart();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pushed = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ bigaMenu: true }, "");
    pushed.current = true;
    const onPop = () => { pushed.current = false; onOpenChange(false); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, onOpenChange]);

  const dismiss = () => {
    if (pushed.current) { pushed.current = false; window.history.back(); }
    onOpenChange(false);
  };
  const go = (to) => {
    pushed.current = false;
    onOpenChange(false);
    navigate(to, { replace: true });
  };
  const after = (fn) => () => { dismiss(); setTimeout(fn, 60); };

  const staff = user?.role === "admin" ? ["Dashboard", "/admin", LayoutDashboard, "mobile-menu-admin"]
    : user?.role === "accountant" ? ["Books", "/admin/accounting", BookOpen, "mobile-menu-books"] : null;

  return (
    <Sheet open={open} onOpenChange={(v) => (v ? onOpenChange(true) : dismiss())}>
      <SheetContent side="right" className="bg-biga-cream w-[86vw] max-w-sm p-5 flex flex-col gap-1 overflow-y-auto" data-testid="mobile-menu">
        <SheetTitle className="font-display font-black text-2xl text-biga-ink pr-8" data-testid="mobile-menu-greeting">
          {user ? `Ciao, ${user.name.split(" ")[0]}` : "BIGA"}
        </SheetTitle>
        <SheetDescription className="sr-only">Site menu</SheetDescription>
        <div className="mt-3 space-y-1">
          {user && <>
            <Row big icon={Receipt} label="My Orders" active={pathname.startsWith("/orders")} onClick={() => go("/orders")} testId="mobile-menu-orders" />
            <Row big icon={User} label="Account" active={pathname.startsWith("/account")} onClick={() => go("/account")} testId="mobile-menu-account" />
            <div className="h-px bg-biga-ink/10 my-2" />
          </>}
          <Row icon={UtensilsCrossed} label="Menu" onClick={after(onMenu)} testId="mobile-menu-menu" />
          <Row icon={Apple} label="Nutrition" active={pathname === "/nutrition"} onClick={() => go("/nutrition")} testId="mobile-menu-nutrition" />
          <Row icon={Images} label="Gallery" active={pathname === "/gallery"} onClick={() => go("/gallery")} testId="mobile-menu-gallery" />
          <Row icon={Info} label="About" active={pathname === "/about"} onClick={() => go("/about")} testId="mobile-menu-about" />
          {staff && <Row icon={staff[2]} label={staff[0]} active={pathname.startsWith(staff[1])} onClick={() => go(staff[1])} testId={staff[3]} />}
          <div className="h-px bg-biga-ink/10 my-2" />
          <Row icon={MessageCircle} label={count > 0 ? "Order on WhatsApp" : "WhatsApp us"} testId="mobile-menu-whatsapp"
            onClick={after(() => (count > 0 ? startWhatsAppCheckout() : window.open(WA_CONTACT_URL, "_blank", "noopener")))} />
          <Row icon={Phone} label="Call the kitchen" testId="mobile-menu-call" onClick={after(() => { window.location.href = PHONE_TEL; })} />
        </div>
        <div className="mt-auto pt-6">
          {user ? (
            <button type="button" onClick={after(logout)} data-testid="mobile-menu-logout"
              className="w-full flex items-center justify-center gap-2 min-h-[52px] rounded-2xl border-2 border-biga-ink/15 font-bold text-biga-ink hover:border-biga-ink">
              <LogOut size={18} /> Log out
            </button>
          ) : (
            <button type="button" onClick={after(onAuthClick)} data-testid="mobile-menu-signin"
              className="w-full flex items-center justify-center gap-2 min-h-[56px] rounded-2xl bg-biga-orange text-biga-cream font-bold text-base hover:bg-biga-green transition-colors">
              <LogIn size={19} /> Sign in
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
